import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import MyMarker from '../../components/Marker';
import { ObjectCard } from '../../components/ObjectCard';
import { Box, IconButton, Dialog } from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { UpdatedTs } from '../../util/FormattingUtils';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const endpoint = "cctvCameras";

// Transparent 1x1 pixel: the cluster visual comes from the .cluster-camera CSS background,
// so the clusterer's injected <img> uses this instead of requesting numbered PNGs (e.g. camera_dome_green1.png).
const TRANSPARENT_PX = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const clusterStyles = [30, 35, 40, 45, 50, 55].map(size => ({
    url: TRANSPARENT_PX,
    width: size,
    height: size,
    textColor: "#ffffff"
}));

const showCardContent = (obj) => { return true }

const ModuleMarker = (props) => {
    return (
        <MyMarker {...props} icondef={getIcon()} />
    )
}

const getIcon = () => {
    return {
        url: "/mapicons/cctv/camera_dome_green.png",
        anchor: new window.google.maps.Point(13, 12),
        scaledSize: new window.google.maps.Size(23, 23)
    };
}

const CardContent = (props) => {
    const obj = props.obj;
    const [rotation, setRotation] = useState(0);
    const [imgStatus, setImgStatus] = useState('loading');
    const [imgSrc, setImgSrc] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
    // Actual snapshot file time, read from the image response's Last-Modified header
    const [snapshotTime, setSnapshotTime] = useState(null);

    const openFullscreen = () => {
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
        setDialogOpen(true);
    };
    const zoomIn = () => setZoom(z => clampZoom(z + ZOOM_STEP));
    const zoomOut = () => setZoom(z => {
        const next = clampZoom(z - ZOOM_STEP);
        if (next === MIN_ZOOM) setOffset({ x: 0, y: 0 });
        return next;
    });
    const onPointerDown = (e) => {
        if (zoom <= MIN_ZOOM) return;
        e.preventDefault();
        dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
        setDragging(true);
    };
    const onPointerMove = (e) => {
        if (!dragging) return;
        setOffset({ x: dragStart.current.ox + (e.clientX - dragStart.current.x), y: dragStart.current.oy + (e.clientY - dragStart.current.y) });
    };
    const endDrag = () => setDragging(false);

    useEffect(() => {
        if (!obj.snapshotFilename) {
            setImgStatus('error');
            return;
        }
        setImgStatus('loading');
        setSnapshotTime(null);

        let objectUrl = null;
        let cancelled = false;
        const url = process.env.REACT_APP_SNAPSHOT_URL + "/" + obj.snapshotFilename;

        fetch(url, { cache: 'no-store' })
            .then(res => {
                if (!res.ok) throw new Error('Snapshot request failed: ' + res.status);
                const lastModified = res.headers.get('Last-Modified');
                if (!cancelled && lastModified) setSnapshotTime(new Date(lastModified));
                return res.blob();
            })
            .then(blob => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImgSrc(objectUrl);
                setImgStatus('ready');
            })
            .catch(() => { if (!cancelled) setImgStatus('error'); });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [obj.snapshotFilename]);

    return (
        <>
            <Box sx={{ fontWeight: 'bold' }}>{obj.description || obj.name}</Box>
            {imgStatus === 'ready' ?
                <Box sx={{ mt: 1 }}>
                    <img
                        src={imgSrc}
                        alt={obj.description || 'CCTV Snapshot'}
                        onClick={() => setDialogOpen(true)}
                        style={{ width: '100%', maxWidth: '200px', cursor: 'pointer', transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease' }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Box sx={{ fontSize: '0.75rem' }}>
                            {snapshotTime ? <UpdatedTs value={snapshotTime} /> : (obj.lastUpdate || '')}
                        </Box>
                        <Box>
                            <IconButton size="small" onClick={() => setRotation(r => r - 90)} title="Rotate left" sx={{ color: '#333' }}>
                                <RotateLeftIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setRotation(r => r + 90)} title="Rotate right" sx={{ color: '#333' }}>
                                <RotateRightIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={openFullscreen} title="Open larger view" sx={{ color: '#333' }}>
                                <FullscreenIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
                : imgStatus === 'error' ?
                <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    Image currently unavailable
                </Box>
                : null}

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="lg"
                PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ fontWeight: 600 }}>{obj.description || obj.name}</Box>
                    <IconButton size="small" onClick={() => setDialogOpen(false)} title="Close">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'action.hover', p: 2, overflow: 'hidden' }}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerLeave={endDrag}
                >
                    <img
                        src={imgSrc}
                        alt={obj.description || 'CCTV Snapshot'}
                        draggable={false}
                        onPointerDown={onPointerDown}
                        style={{ display: 'block', maxWidth: '80vw', maxHeight: '70vh', borderRadius: 6, transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`, transition: dragging ? 'none' : 'transform 0.3s ease', cursor: zoom > MIN_ZOOM ? (dragging ? 'grabbing' : 'grab') : 'default', userSelect: 'none', touchAction: 'none' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {snapshotTime ? <UpdatedTs value={snapshotTime} /> : (obj.lastUpdate || '')}
                    </Box>
                    <Box>
                        <IconButton size="small" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} title="Zoom out">
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} title="Zoom in">
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setRotation(r => r - 90)} title="Rotate left">
                            <RotateLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setRotation(r => r + 90)} title="Rotate right">
                            <RotateRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    )
}

const InfoWindow = (props) => {
    const module = props.module;
    const obj = props.icon;
    const MyCardContent = module.cardContent;
    const showCardContent = module.showCardContent;

    return (
        <div className="infoWindow center map-popup-text"><div>CCTV Camera</div>
            {showCardContent(obj) ?
                <div className="spacer"><MyCardContent obj={obj} /></div>
                :
                null}
        </div>
    )
}

export default {
    name: 'Cameras',
    label: 'Camera #',
    marker: ModuleMarker,
    showOnMap: true,
    mapLayerIcon: "/mapicons/cctv/camera_dome_green.png",
    showOnSidebar: false,
    sideBarCard: ObjectCard,
    endpoint: endpoint,
    defaultOn: false,
    updateDelay: 0,
    showCardContent: showCardContent,
    cardContent: CardContent,
    infoWindow: InfoWindow,
    cluster: true,
    clusterMarker: "/mapicons/cctv/camera_dome_green",
    clusterClass: "cluster-camera",
    clusterStyles: clusterStyles,
    publishLayer: "tasWebapp.cctvCameras.enable"
}
